import { AVAILABLE_SUPPORTED_CURRENCIES } from '@app/common/constants';
import Text from '@app/components/ui/Text/Text';
import { currencyFormatter } from '@app/utils/pricingCurrencyUtils';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJs,
  Legend,
  LinearScale,
  Tooltip,
  type ChartOptions,
  type TooltipItem,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { LuStethoscope } from 'react-icons/lu';
import { RiNurseFill } from 'react-icons/ri';
import { FinanceIncomeExpensesFormattedRow } from './types';

ChartJs.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

type IncomeVsExpensesChartProps = {
  rows: FinanceIncomeExpensesFormattedRow[];
  locale: string;
};

const barCount = 3;
const minVisibleBarLength = 10;
const estimatedBarWidth = 28;
const groupWidth = barCount * estimatedBarWidth;
const categoryPadding = 16;
const categoryWidth = groupWidth + categoryPadding;

// these two below change the width of the bars and the gap between them
const categoryPercentage = 0.75;
const barPercentage = 0.9;

const IncomeVsExpensesChart = ({ rows, locale }: IncomeVsExpensesChartProps) => {
  const { t } = useTranslation();
  const chartCurrency = AVAILABLE_SUPPORTED_CURRENCIES.EURO;

  const labels = rows.map((row) => row.monthLabel);

  const chartMinWidth = Math.max(480, labels.length * categoryWidth);

  const baseBarStyle = {
    borderRadius: 6,
    borderSkipped: false,
    categoryPercentage,
    barPercentage,
    minBarLength: minVisibleBarLength,
  };

  const chartData = {
    labels,
    datasets: [
      {
        label: t('income'),
        data: rows.map((row) => row.income),
        backgroundColor: '#22c55e',
        ...baseBarStyle,
      },
      {
        label: t('doctors'),
        data: rows.map((row) => row.doctors),
        backgroundColor: '#e2e8f0',
        ...baseBarStyle,
      },
      {
        label: t('caregivers'),
        data: rows.map((row) => row.caregivers),
        backgroundColor: '#7dd3fc',
        ...baseBarStyle,
      },
    ],
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 24, right: 8, bottom: 0, left: 8 },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: TooltipItem<'bar'>) => {
            const label = context.dataset.label ?? '';
            const value = typeof context.parsed.y === 'number' ? context.parsed.y : 0;
            return `${label}: ${currencyFormatter(Math.abs(value), chartCurrency, locale)}`;
          },
        },
      },
      datalabels: {
        anchor: 'end',
        align: 'top',
        offset: 6,
        color: '#64748b',
        font: {
          size: 11,
          family: 'Inter, system-ui, -apple-system, Segoe UI',
          weight: 'normal',
        },
        formatter: (value: unknown) => {
          const numberValue = typeof value === 'number' ? value : 0;
          return currencyFormatter(Math.abs(numberValue), chartCurrency, locale, {
            isCompact: true,
            hasDecimals: true,
          });
        },
        clip: false,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: '#64748b',
          font: {
            size: 12,
            family: 'Inter, system-ui, -apple-system, Segoe UI',
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: { display: false },
        border: { display: false },
        ticks: { display: false },
      },
    },
  };

  return (
    <div className="w-full">
      <div className="mb-2 flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-2">
          <span className="text-[18px] leading-none text-emerald-500">€</span>
          <Text type="body2" transKey="income" color="#64748b" />
        </div>
        <div className="flex items-center gap-2">
          <LuStethoscope size={18} color="#94a3b8" />
          <Text type="body2" transKey="doctors" color="#64748b" />
        </div>
        <div className="flex items-center gap-2">
          <RiNurseFill size={18} color="#7dd3fc" />
          <Text type="body2" transKey="caregivers" color="#64748b" />
        </div>
      </div>

      <div className="h-[240px] w-full overflow-x-auto">
        <div className="h-full" style={{ minWidth: chartMinWidth }}>
          <Bar data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
};

export default IncomeVsExpensesChart;
